import React, {useCallback} from 'react';

import {RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ConversationTeamStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {getConversationFinder} from '../../../../../../srcCommon/inboxes/search/cellsSearchResultsSelectors';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import {isConversationInTeamContextOrSelfAssigned} from '../../../../../helpers/models/conversationHelpers';
import {areConversationSafetyNetsSatisfied} from '../../../../../helpers/safetyNetsHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {SafetyNetsEnum} from '../../../../../models/api/safetyNetModel';
import {requiredTaggingActions, SafetyNetActionsEnum, useSafetyNets} from './safetyNetsContext';

/*
 * Types.
 */

type SafetyNetHandler<TArgs extends any[]> = (...args: TArgs) => void;

/*
 * Props.
 */

interface SafetyNetsCoordinatorProps<TArgs extends any[]> {
  conversations: ReadonlyArray<ConversationBaseModel>;
  onActionValidated: SafetyNetHandler<TArgs>;
  safetyNetActionType: SafetyNetActionsEnum;
  children: RendererOf<SafetyNetHandler<TArgs>>;
  cellsContext?: CellsContext;
}

/*
 * Component.
 */

export const SafetyNetsCoordinator = <TArgs extends any[]>(props: SafetyNetsCoordinatorProps<TArgs>) => {
  const {onActionValidated, safetyNetActionType, conversations, cellsContext, children} = props;

  const {onShowBlockingModal} = useSafetyNets();

  const onAction = useCallback(
    (...args: TArgs) => {
      const onActionValidatedCallback = () => onActionValidated(...args);

      const blockedConversations = filterConversationsSafetyNetApplying(
        conversations,
        safetyNetActionType,
        cellsContext,
      );

      if (blockedConversations.length !== 0) {
        onShowBlockingModal({
          type: safetyNetActionType,
          onActionValidated: onActionValidatedCallback,
          conversations: blockedConversations,
        });
        return;
      }

      onActionValidatedCallback();
    },
    [cellsContext, conversations, onActionValidated, onShowBlockingModal, safetyNetActionType],
  );

  return <>{children(onAction)}</>;
};

/*
 * Helpers.
 */

function filterConversationsSafetyNetApplying<T extends ConversationBaseModel>(
  conversations: ReadonlyArray<T>,
  safetyNetActionType: SafetyNetActionsEnum,
  cellsContext?: CellsContext,
): ReadonlyArray<T> {
  if (!requiredTaggingActions.includes(safetyNetActionType)) {
    return [];
  }

  return conversations
    .filter(archiveFilter(safetyNetActionType))
    .filter(sendAndArchiveFilter(safetyNetActionType))
    .filter(commentAndArchiveFilter(safetyNetActionType))
    .filter(unsatisfiedRequiredTaggingFilter(safetyNetActionType, cellsContext));
}

type ConversationFilterPredicate = (conversation: ConversationBaseModel) => boolean;
const truePredicate: ConversationFilterPredicate = () => true;

function archiveFilter(safetyNetActionType: SafetyNetActionsEnum): ConversationFilterPredicate {
  if (safetyNetActionType !== SafetyNetActionsEnum.ARCHIVE) {
    return truePredicate;
  }

  // If action is archive and conversation is not open, required tagging does not apply.
  return (conversation) => conversation.teamStatus === ConversationTeamStatusesEnum.OPEN;
}

function sendAndArchiveFilter(safetyNetActionType: SafetyNetActionsEnum): ConversationFilterPredicate {
  if (safetyNetActionType !== SafetyNetActionsEnum.SEND_AND_ARCHIVE) {
    return truePredicate;
  }

  // If a the conversation is new, required tagging does not apply.
  return (conversation) => conversation.teamStatus !== ConversationTeamStatusesEnum.INVISIBLE;
}

function commentAndArchiveFilter(safetyNetActionType: SafetyNetActionsEnum): ConversationFilterPredicate {
  if (safetyNetActionType !== SafetyNetActionsEnum.COMMENT_AND_ARCHIVE) {
    return truePredicate;
  }

  // If action is archive and conversation is not open, required tagging does not apply.
  return (conversation) => conversation.teamStatus === ConversationTeamStatusesEnum.OPEN;
}

function unsatisfiedRequiredTaggingFilter(
  safetyNetActionType: SafetyNetActionsEnum,
  cellsContext?: CellsContext,
) {
  return (conversation: ConversationBaseModel) => {
    // If action is tracker_archive, ignore required tagging.
    if (
      [SafetyNetActionsEnum.ARCHIVE, SafetyNetActionsEnum.COMMENT_AND_ARCHIVE].includes(
        safetyNetActionType,
      ) &&
      cellsContext &&
      !isConversationInTeamContextOrSelfAssigned(conversation, cellsContext)
    ) {
      return false;
    }

    return !areConversationSafetyNetsSatisfied(conversation, SafetyNetsEnum.REQUIRED_TAGGING);
  };
}

type GetConversationIdsFromArgs<TArgs extends any[]> = (...args: TArgs) => ReadonlyArray<number>;

/**
 * This hook handles the logic of SafetyNets over an handler.
 * It takes care of calling SafetyNets before the handler.
 * @param safetyNetAction The SafetyNet action.
 * @param handler The handler.
 * @param getConversationIdsFromArgs A function extracting conversation ids from the handler arguments.
 * @returns The new handler with SafetyNets.
 */
export function useConversationIdsHandlerWithSafetyNets<TArgs extends any[]>(
  safetyNetAction: SafetyNetActionsEnum,
  handler: SafetyNetHandler<TArgs>,
  getConversationIdsFromArgs: GetConversationIdsFromArgs<TArgs>,
  cellsContext?: CellsContext,
) {
  const {onShowBlockingModal} = useSafetyNets();
  const dispatch = useAppDispatch();

  return useCallback(
    (...args: TArgs) => {
      const conversationIds = getConversationIdsFromArgs(...args);

      dispatch((_dispatch, getState) => {
        const state = getState();
        const conversationFinder = getConversationFinder(state);

        const conversations = conversationIds
          .map((conversationId) => conversationFinder(conversationId))
          .filter(isDefined);

        const blockedConversations = filterConversationsSafetyNetApplying(
          conversations,
          safetyNetAction,
          cellsContext,
        );

        if (blockedConversations.length !== 0) {
          onShowBlockingModal({
            type: safetyNetAction,
            onActionValidated: () => handler(...args),
            conversations: blockedConversations,
          });
          return;
        }

        handler(...args);
      });
    },
    [cellsContext, dispatch, getConversationIdsFromArgs, handler, onShowBlockingModal, safetyNetAction],
  );
}
